import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

const ROHAI_MOVEMENT_STEPS = [
  "Begin in ready stance facing D",
  "Step left foot to B forming a left back stance while executing a knife-hand guarding block",
  "Execute a high punch with the right fist while maintaining the left back stance",
  "Step right foot to B forming a right walking stance while executing a high punch with the right fist",
  "Move the right foot to A, turning clockwise to form a right back stance while executing a knife-hand guarding block",
  "Execute a high punch with the left fist while maintaining the right back stance",
  "Step left foot to A forming a left walking stance while executing a high punch with the left fist",
  "Move into crane stance, right leg raised",
  "Execute a side piercing kick with the right foot",
  "Lower the right foot to D forming a right walking stance while executing a middle punch with the right fist",
  "Execute a middle punch with the left fist while maintaining the right walking stance",
  "Execute a middle punch with the right fist while maintaining the right walking stance",
  "Move the right foot to C, turning clockwise to form a left back stance while executing a knife-hand guarding block",
  "Execute a high punch with the right fist while maintaining the left back stance",
  "Step right foot to C forming a right walking stance while executing a high punch with the right fist",
  "Move into crane stance, left leg raised",
  "Execute a side piercing kick with the left foot",
  "Lower the left foot to C forming a left walking stance while executing a middle punch with the left fist",
  "Execute a middle punch with the right fist while maintaining the left walking stance",
  "Execute a middle punch with the left fist while maintaining the left walking stance",
  "Move the left foot to B, turning counter-clockwise to form a right back stance while executing a knife-hand guarding block",
  "Execute a high punch with the left fist while maintaining the right back stance",
  "Step left foot to B forming a left walking stance while executing a high punch with the left fist",
  "Move into crane stance, right leg raised",
  "Execute a side piercing kick with the right foot",
  "Lower the right foot to B forming a right walking stance while executing a middle punch with the right fist KIHAP",
  "Move the right foot back to ready stance",
  "Bow",
] as const;

interface RohaiProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

function RohaiMovementList() {
  return (
    <ol className="list-none space-y-2 pl-2">
      {ROHAI_MOVEMENT_STEPS.map((step, index) => (
        <li key={index} className="flex">
          <span className="mr-2 text-taekwondo-red">{index + 1}.</span>
          <span className="text-gray-700">
            {step.includes("KIHAP") ? (
              <>
                {step.replace(" KIHAP", "")} <span className="font-semibold">KIHAP</span>
              </>
            ) : (
              step
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

export default function Rohai({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: RohaiProps) {
  if (!["NAVY", "BLACK/WHITE", "BLACK/D1", "BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const patternInformationIntro = (
    <div>
      <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern Information</h4>
      <p className="mb-4 text-gray-700">
        Rohai, meaning &quot;Vision of a Crane,&quot; is a pattern that embodies the grace and precision of the crane&apos;s movements. This pattern
        emphasizes balance, precision, and the ability to transition between soft and hard techniques.
      </p>
    </div>
  );

  const patternInformation = (
    <div>
      {hasDemo?.(["rohai"]) && (
        <button
          type="button"
          className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
          onClick={() => onWatchDemo?.(["rohai"])}
        >
          Watch Demonstration Video
        </button>
      )}
      <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern Information</h4>
      <p className="mb-4 text-gray-700">
        Rohai, meaning &quot;Vision of a Crane,&quot; is a pattern that embodies the grace and precision of the crane&apos;s movements. This pattern
        emphasizes balance, precision, and the ability to transition between soft and hard techniques.
      </p>
    </div>
  );

  const patternDetails = (
    <div>
      <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern Details</h4>
      <ul className="list-disc space-y-1 pl-5 text-gray-700">
        <li>Number of Movements: 28</li>
        <li>Pattern Diagram: Cross (+)</li>
        <li>Starting Position: Ready Stance</li>
      </ul>
    </div>
  );

  const keyTechniques = (
    <div>
      <h4 className="mb-2 text-base font-medium text-taekwondo-red">Key Techniques</h4>
      <ul className="list-disc space-y-1 pl-5 text-gray-700">
        <li>Crane stance variations</li>
        <li>Precise striking methods</li>
        <li>Balance transitions</li>
        <li>Soft-hard combinations</li>
        <li>Circular blocking patterns</li>
        <li>Fluid movement sequences</li>
      </ul>
    </div>
  );

  const trainingFocus = (
    <div>
      <h4 className="mb-2 text-base font-medium text-taekwondo-red">Training Focus</h4>
      <ul className="list-disc space-y-1 pl-5 text-gray-700">
        <li>Balance development</li>
        <li>Precision control</li>
        <li>Energy efficiency</li>
        <li>Visual awareness</li>
        <li>Graceful execution</li>
        <li>Strategic positioning</li>
      </ul>
    </div>
  );

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">Rohai — Vision of a Crane</h2>
        {hasDemo?.(["rohai"]) && (
          <div className="mb-3">
            <button
              type="button"
              className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
              onClick={() => onWatchDemo?.(["rohai"])}
            >
              Watch Demonstration Video
            </button>
          </div>
        )}
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="Rohai">
            <TabsTrigger value="pattern-information" className={triggerClass}>
              Pattern Information
            </TabsTrigger>
            <TabsTrigger value="pattern-details" className={triggerClass}>
              Pattern Details
            </TabsTrigger>
            <TabsTrigger value="key-techniques" className={triggerClass}>
              Key Techniques
            </TabsTrigger>
            <TabsTrigger value="pattern" className={triggerClass}>
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            {patternInformationIntro}
          </TabsContent>
          <TabsContent value="pattern-details" className="mt-0 focus-visible:outline-none">
            {patternDetails}
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            {keyTechniques}
          </TabsContent>
          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Movements</h4>
              <RohaiMovementList />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        {patternInformation}
        {patternDetails}
        {keyTechniques}
        <div>
          <h4 className="mb-2 text-base font-medium text-taekwondo-red">Movements</h4>
          <RohaiMovementList />
        </div>
        {trainingFocus}
      </div>
    </div>
  );
}
